import { renderEmailBodyHtml } from '@uit/shared/email-template';
import {
  RCF_EMAIL_PLACEHOLDERS,
  type RcfInternalRecipient,
  type RcfInternalRecipientsResponse,
  RcfInternalRecipientsResponse as RcfInternalRecipientsResponseSchema,
  type RcfSettings,
  RcfSettings as RcfSettingsSchema,
} from '@uit/shared/schemas/rcf';
import { useEffect, useRef, useState } from 'react';
import { useApi } from '../../lib/api.js';
import s from './rcf-settings.module.scss';

// Friendly labels for the RCF fields, so a validation error names the field
// the admin actually sees rather than the raw key.
const FIELD_LABELS: Record<string, string> = {
  sow_number: 'SOW number',
  company_code: 'Company code',
  location_address: 'Location address',
  wand_email: 'WAND',
  email_subject: 'Subject',
  email_body: 'Body',
};

// Turn the API's validation response into a field-specific message.
function validationMessage(body: unknown): string | null {
  const issues = (body as { issues?: Array<{ path?: unknown[]; message?: string }> })?.issues;
  if (!Array.isArray(issues) || issues.length === 0) return null;
  const first = issues[0]!;
  const field = typeof first.path?.[0] === 'string' ? (first.path[0] as string) : '';
  const label = FIELD_LABELS[field] ?? field;
  return `${label}: ${first.message ?? 'invalid value'}.`;
}

// Sample values for the per-resource RCF placeholders so the preview matches
// what gets sent. The real RCF fills them from the engagement + resource.
const PREVIEW_SAMPLE: Record<string, string> = {
  resourceName: 'Christopher Wu',
  startDate: '07/01/2026',
  resourceAddress: '88 Prefill Rd, Stamford, CT 06901',
  resourcePhone: '203-555-0142',
  uitEmail: 'ChristopherWu@ultimumit.com',
};

function subject(template: string, vars: Record<string, string>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (m, k: string) => vars[k] ?? m);
}

// RCF email Settings (T-113): SOW #, the fixed recipients (WAND/Francine/Eric;
// manager + principal are added per-RCF), and the markdown template + preview
// reusing the onboarding-email renderer.
export function RcfEmailSection() {
  const { data, refetch } = useApi<RcfSettings>('/api/settings/rcf', RcfSettingsSchema);
  const [draft, setDraft] = useState<RcfSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  // Internal uIT recipients are a managed list (table CRUD), separate from the
  // RcfSettings PATCH — fetched + mutated on their own endpoints.
  const { data: internalData, refetch: refetchInternal } = useApi<RcfInternalRecipientsResponse>(
    '/api/settings/rcf-internal-recipients',
    RcfInternalRecipientsResponseSchema,
  );
  const [intName, setIntName] = useState('');
  const [intEmail, setIntEmail] = useState('');
  const internal: RcfInternalRecipient[] = internalData?.recipients ?? [];

  async function addInternal(): Promise<void> {
    if (!intEmail.trim()) return;
    await fetch('/api/settings/rcf-internal-recipients', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ name: intName.trim() || undefined, email: intEmail.trim() }),
    });
    setIntName('');
    setIntEmail('');
    void refetchInternal();
  }
  async function removeInternal(id: string): Promise<void> {
    await fetch(`/api/settings/rcf-internal-recipients/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    void refetchInternal();
  }

  useEffect(() => {
    if (data) setDraft(data);
  }, [data]);

  if (!draft) {
    return (
      <section className={s.section}>
        <h2 className={s.sectionTitle}>RCF email</h2>
        <p className={s.sectionSub}>Loading…</p>
      </section>
    );
  }

  const update = (patch: Partial<RcfSettings>): void => {
    setDraft((d) => (d ? { ...d, ...patch } : d));
    setSuccess(null);
  };

  const insertPlaceholder = (p: string): void => {
    const ta = bodyRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const tok = `{{${p}}}`;
    update({ email_body: draft.email_body.slice(0, start) + tok + draft.email_body.slice(end) });
    requestAnimationFrame(() => {
      ta.focus();
      ta.selectionStart = ta.selectionEnd = start + tok.length;
    });
  };

  const save = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const r = await fetch('/api/settings/rcf', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(draft),
      });
      if (!r.ok) {
        if (r.status === 403) {
          setError('You do not have permission.');
          return;
        }
        const body = (await r.json().catch(() => ({}))) as unknown;
        setError(validationMessage(body) ?? `Could not save (${r.status}).`);
        return;
      }
      setDraft(RcfSettingsSchema.parse(await r.json()));
      setSuccess('Saved.');
      void refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  const previewSubject = subject(draft.email_subject, PREVIEW_SAMPLE);
  const previewHtml = renderEmailBodyHtml(draft.email_body, PREVIEW_SAMPLE);
  const recipientsLine = [
    draft.wand_email || 'WAND <unset>',
    `${PREVIEW_SAMPLE['resourceName']}'s manager`,
    "manager's principal",
    ...internal.map((r) => r.email),
  ].join(', ');

  return (
    <section className={s.section}>
      <h2 className={s.sectionTitle}>RCF email</h2>
      <p className={s.sectionSub}>
        The SOW number, Company Code, Location address, fixed recipients, and message body for the
        Resource Change Form (T-115). Sent via the portal&rsquo;s existing email service with the RCF
        file attached.
      </p>

      <div className={s.row}>
        <div className={s.fieldBlock}>
          <label className={s.label} htmlFor="rcf-sow">
            SOW number
          </label>
          <input
            id="rcf-sow"
            className={s.input}
            value={draft.sow_number}
            onChange={(e) => update({ sow_number: e.target.value })}
          />
          <div className={s.hint}>Stamped on every RCF (constant today, editable here).</div>
        </div>
        <div className={s.fieldBlock}>
          <label className={s.label} htmlFor="rcf-company">
            Company code
          </label>
          <input
            id="rcf-company"
            className={s.input}
            value={draft.company_code}
            onChange={(e) => update({ company_code: e.target.value })}
          />
          <div className={s.hint}>Cigna Company Code cell (e.g. 352-Priority Healthcare Distribution).</div>
        </div>
        <div className={s.fieldBlock}>
          <label className={s.label} htmlFor="rcf-location">
            Location address
          </label>
          <input
            id="rcf-location"
            className={s.input}
            value={draft.location_address}
            onChange={(e) => update({ location_address: e.target.value })}
          />
          <div className={s.hint}>Location address cell on the RCF (e.g. Lake Mary, FL).</div>
        </div>
      </div>

      <div className={s.previewLabel}>WAND (Cigna contingent-labor support)</div>
      <div className={s.fieldBlock}>
        <label className={s.label} htmlFor="rcf-wand">
          WAND email
        </label>
        <input
          id="rcf-wand"
          type="email"
          className={s.input}
          value={draft.wand_email}
          onChange={(e) => update({ wand_email: e.target.value })}
        />
        <div className={s.hint}>Cigna&rsquo;s contingent-labor support shared mailbox (Evernorth).</div>
      </div>

      <div className={s.previewLabel}>Internal uIT recipients</div>
      <div className={s.hint} style={{ marginBottom: 8 }}>
        uIT people CC&rsquo;d on every RCF. The Cigna manager + their principal are added per-RCF.
      </div>
      {internal.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 12px' }}>
          {internal.map((r) => (
            <li
              key={r.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '6px 10px',
                border: '1px solid #e4e4e7',
                borderRadius: 4,
                marginBottom: 6,
                fontSize: 14,
              }}
            >
              <span>
                {r.name ? `${r.name} · ` : ''}
                {r.email}
              </span>
              <button
                type="button"
                className={s.saveBtn}
                style={{
                  background: 'transparent',
                  color: '#dc2626',
                  borderColor: '#dc2626',
                  padding: '2px 10px',
                }}
                onClick={() => void removeInternal(r.id)}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className={s.row}>
        <div className={s.fieldBlock}>
          <label className={s.label} htmlFor="rcf-int-name">
            Name (optional)
          </label>
          <input
            id="rcf-int-name"
            className={s.input}
            value={intName}
            onChange={(e) => setIntName(e.target.value)}
          />
        </div>
        <div className={s.fieldBlock}>
          <label className={s.label} htmlFor="rcf-int-email">
            Email
          </label>
          <input
            id="rcf-int-email"
            type="email"
            className={s.input}
            value={intEmail}
            onChange={(e) => setIntEmail(e.target.value)}
          />
        </div>
        <div className={s.fieldBlock} style={{ display: 'flex', alignItems: 'flex-end' }}>
          <button type="button" className={s.saveBtn} onClick={() => void addInternal()}>
            Add recipient
          </button>
        </div>
      </div>

      <div className={s.previewLabel}>Message template</div>
      <div className={s.fieldBlock}>
        <label className={s.label} htmlFor="rcf-subject">
          Subject
        </label>
        <input
          id="rcf-subject"
          className={s.input}
          value={draft.email_subject}
          onChange={(e) => update({ email_subject: e.target.value })}
        />
      </div>
      <div className={s.fieldBlock}>
        <label className={s.label} htmlFor="rcf-body">
          Body (markdown)
        </label>
        <div className={s.phs}>
          {RCF_EMAIL_PLACEHOLDERS.map((p) => (
            <button key={p} type="button" className={s.ph} onClick={() => insertPlaceholder(p)}>
              {`{{${p}}}`}
            </button>
          ))}
        </div>
        <textarea
          id="rcf-body"
          ref={bodyRef}
          className={s.textarea}
          value={draft.email_body}
          onChange={(e) => update({ email_body: e.target.value })}
        />
      </div>

      <button type="button" className={s.saveBtn} disabled={saving} onClick={() => void save()}>
        {saving ? 'Saving…' : 'Save RCF email'}
      </button>
      {success && (
        <div className={s.success} role="status">
          {success}
        </div>
      )}
      {error && (
        <div className={s.error} role="alert">
          {error}
        </div>
      )}

      <div className={s.previewLabel}>Preview · what recipients see</div>
      <div className={s.emailCard}>
        <div className={s.emailHead}>
          <div>To {recipientsLine}</div>
          <div>Subject {previewSubject}</div>
        </div>
        {/* previewHtml is markdown-it html:false output with escaped values — safe. */}
        <div className={s.emailBody} dangerouslySetInnerHTML={{ __html: previewHtml }} />
        <div className={s.attach}>
          📎 ResourceChangeForm_{PREVIEW_SAMPLE['resourceName']}.xlsx — generated + attached
          automatically (T-115).
        </div>
      </div>
    </section>
  );
}
